import React from 'react';
import type { ShortInterestWidgetConfig } from './types';
import { definition } from './index';

interface ShortInterestWidgetBuilderProps {
  config: Partial<ShortInterestWidgetConfig>;
  onChange: (config: ShortInterestWidgetConfig) => void;
}

const SHORT_INTEREST_SOURCES: Array<{ value: NonNullable<ShortInterestWidgetConfig['short_interest_provider']>; label: string }> = [
  { value: 'AUTO', label: 'Auto (best available)' },
  { value: 'FINRA', label: 'FINRA consolidated' },
];

const PRICE_SOURCES: Array<{ value: NonNullable<ShortInterestWidgetConfig['price_provider']>; label: string }> = [
  { value: 'AUTO', label: 'Auto (best available)' },
  { value: 'FMP', label: 'FMP' },
  { value: 'INSYNC', label: 'INSYNC' },
];

const ShortInterestWidgetBuilder: React.FC<ShortInterestWidgetBuilderProps> = ({ config, onChange }) => {
  const shortSource = config.short_interest_provider ?? 'AUTO';
  const priceSource = config.price_provider ?? 'AUTO';

  return (
    <div className="space-y-4">
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 text-sm text-warning-800">
        <strong>{definition.meta.label}</strong>
        <p className="mt-1">{definition.meta.description}</p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Short-interest source</label>
          <select
            value={shortSource}
            onChange={(e) => onChange({ ...config, short_interest_provider: e.target.value as ShortInterestWidgetConfig['short_interest_provider'] })}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
          >
            {SHORT_INTEREST_SOURCES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Price / context source</label>
          <select
            value={priceSource}
            onChange={(e) => onChange({ ...config, price_provider: e.target.value as ShortInterestWidgetConfig['price_provider'] })}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
          >
            {PRICE_SOURCES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-primary-50 border-primary-200">
        <h4 className="font-medium text-primary-800 mb-2">How it works</h4>
        <ul className="text-sm text-primary-700 space-y-1 ml-5 list-disc">
          <li>Short interest and price context use independently selectable sources</li>
          <li>Shares outstanding is resolved from SEC (public) to compute SI % of float</li>
          <li>FINRA requires credentials (or the platform FINRA key) to be configured</li>
          <li>SI % falls back to a market-cap / price estimate when SEC is unavailable</li>
        </ul>
      </div>
    </div>
  );
};

export default ShortInterestWidgetBuilder;
